/** Shared presentational components used across every page. */
import { colors } from '../theme';
import { STATUS_COLORS } from '../constants';
import { titleCase, initials } from '../utils/format';

export const Button = ({
  title, children, onClick, variant = 'primary', size = 'md', loading,
  disabled, icon, full, type = 'button', className = '', style,
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    style={style}
    className={`btn btn-${variant} ${size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : ''} ${full ? 'full' : ''} ${className}`}
  >
    {loading ? <span className="spinner" /> : (
      <>
        {icon ? <span aria-hidden="true">{icon}</span> : null}
        <span>{title || children}</span>
      </>
    )}
  </button>
);

export const Input = ({
  label, error, icon, hint, textarea, rows = 4, className = '', style, inputStyle, ...props
}) => (
  <div className={`field ${className}`} style={style}>
    {label ? <label className="field-label">{label}</label> : null}
    <div className={`input-wrap ${error ? 'error' : ''}`}>
      {icon ? <span aria-hidden="true" style={{ fontSize: 16 }}>{icon}</span> : null}
      {textarea
        ? <textarea rows={rows} style={inputStyle} {...props} />
        : <input style={inputStyle} {...props} />}
    </div>
    {error ? <div className="field-error">{error}</div> : null}
    {hint && !error ? <div className="field-hint">{hint}</div> : null}
  </div>
);

/** Chip-style radio group - the web stand-in for the app's Chip rows. */
export const Select = ({ label, value, onChange, options, hint, className = '' }) => (
  <div className={`field ${className}`}>
    {label ? <label className="field-label">{label}</label> : null}
    <div className="row wrap" style={{ gap: 8 }}>
      {options.map((o) => (
        <Chip
          key={String(o.value)}
          label={o.label}
          icon={o.icon}
          active={String(value) === String(o.value)}
          onClick={() => onChange(o.value)}
        />
      ))}
    </div>
    {hint ? <div className="field-hint">{hint}</div> : null}
  </div>
);

export const Card = ({ children, className = '', elevation = 'sm', onClick, style }) => {
  const cls = `card sh-${elevation} ${onClick ? 'press hoverable' : ''} ${className}`;
  if (!onClick) return <div className={cls} style={style}>{children}</div>;
  return (
    <div className={cls} style={style} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(e); }}>
      {children}
    </div>
  );
};

export const Badge = ({ value, color, bg, style }) => {
  const tone = color || STATUS_COLORS[value] || colors.muted;
  return (
    <span className="badge" style={{ background: bg || `${tone}1A`, color: tone, ...style }}>
      <i className="dot" style={{ background: tone }} />
      {titleCase(value)}
    </span>
  );
};

export const Chip = ({ label, active, onClick, icon, style, className = '' }) => (
  <button type="button" onClick={onClick} style={style} className={`chip ${active ? 'active' : ''} ${className}`}>
    {icon ? <span aria-hidden="true">{icon}</span> : null}
    {label}
  </button>
);

export const Avatar = ({ name, uri, size = 44, style }) => {
  if (uri) {
    return (
      <img
        src={uri}
        alt={name || ''}
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', background: colors.bg, flex: '0 0 auto', ...style,
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%', flex: '0 0 auto',
        background: colors.primaryLight, color: colors.primary,
        display: 'grid', placeItems: 'center',
        fontWeight: 800, fontSize: size * 0.34, ...style,
      }}
    >
      {initials(name)}
    </div>
  );
};

export const Rating = ({ value = 0, count, size = 12, style }) => (
  <span className="row" style={{ gap: 3, ...style }}>
    <span style={{ fontSize: size, color: colors.star }}>★</span>
    <span style={{ fontSize: size, fontWeight: 700, color: colors.ink }}>
      {Number(value || 0).toFixed(1)}
    </span>
    {count !== undefined ? (
      <span style={{ fontSize: size - 1, color: colors.muted }}>({count})</span>
    ) : null}
  </span>
);

export const SectionHeader = ({ title, subtitle, actionLabel, onAction, style }) => (
  <div className="section-head" style={style}>
    <div className="grow">
      <h3 className="h3">{title}</h3>
      {subtitle ? <div className="t-small" style={{ marginTop: 2 }}>{subtitle}</div> : null}
    </div>
    {actionLabel ? (
      <button type="button" className="link-btn" onClick={onAction}>{actionLabel}</button>
    ) : null}
  </div>
);

export const Empty = ({
  emoji = '📭', title = 'Nothing here yet', text, actionLabel, onAction, style,
}) => (
  <div className="center-page reveal" style={style}>
    <div>
      <div style={{ fontSize: 46, marginBottom: 12 }}>{emoji}</div>
      <h3 className="h3">{title}</h3>
      {text ? (
        <p className="t-small" style={{ marginTop: 6, lineHeight: 1.55, maxWidth: 360 }}>{text}</p>
      ) : null}
      {actionLabel ? (
        <Button title={actionLabel} onClick={onAction} style={{ marginTop: 16 }} />
      ) : null}
    </div>
  </div>
);

export const Loading = ({ text = 'Loading…', style }) => (
  <div className="center-page" style={style}>
    <div>
      <span className="spinner lg" style={{ color: colors.primary }} />
      <div className="t-small" style={{ marginTop: 12 }}>{text}</div>
    </div>
  </div>
);

export const Divider = ({ style }) => <div className="divider" style={style} />;

export const Row = ({ label, value, style, valueStyle }) => (
  <div className="row" style={{ padding: '9px 0', gap: 12, alignItems: 'flex-start', ...style }}>
    <div className="t-small grow">{label}</div>
    <div
      className="t-body"
      style={{ fontWeight: 600, textAlign: 'right', whiteSpace: 'pre-line', ...valueStyle }}
    >
      {value}
    </div>
  </div>
);

export const Skeleton = ({ height = 16, width = '100%', radius = 12, style }) => (
  <div className="skeleton" style={{ height, width, borderRadius: radius, ...style }} />
);

export const Modal = ({ open, onClose, children, wide, title }) => {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`modal ${wide ? 'wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {title ? (
          <div className="row-between" style={{ marginBottom: 14 }}>
            <h3 className="h3">{title}</h3>
            <button type="button" className="link-btn" onClick={onClose}>Close ✕</button>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
};

/** Quantity stepper shared by the cart, product page and booking form. */
export const Stepper = ({ value, onChange, min = 0, disabled }) => (
  <div
    className="row"
    style={{
      gap: 0, border: `1px solid ${colors.primary}`, borderRadius: 8,
      background: colors.primaryLight, overflow: 'hidden',
    }}
  >
    <button
      type="button"
      disabled={disabled || value <= min}
      onClick={() => onChange(value - 1)}
      style={{ background: 'none', border: 0, padding: '6px 11px', fontSize: 16, fontWeight: 800, color: colors.primary }}
    >
      −
    </button>
    <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 800, color: colors.primary }}>{value}</span>
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(value + 1)}
      style={{ background: 'none', border: 0, padding: '6px 11px', fontSize: 16, fontWeight: 800, color: colors.primary }}
    >
      +
    </button>
  </div>
);

const Ui = {
  Button, Input, Select, Card, Badge, Chip, Avatar, Rating, SectionHeader,
  Empty, Loading, Divider, Row, Skeleton, Modal, Stepper,
};
export default Ui;
